import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Clock, DollarSign, Users, Calendar } from 'lucide-react';
import SoundTherapyBooking from './SoundTherapyBooking';
import { userApi, mainAdminApi } from '../../../config';
import toast from 'react-hot-toast';

interface UserDetails {
  name: string;
  email: string;
  age: string;
  phone: string;
}

interface SoundTherapySession {
  _id: string;
  instructorName: string;
  timing: string;
  day: string;
  date: string;
  price: number;
  therapyType: string;
  title?: string;
}

const SoundTherapyDetailsPage = () => {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [sessions, setSessions] = useState<SoundTherapySession[]>([]);
  const [selectedSession, setSelectedSession] = useState<SoundTherapySession | null>(null);

  const fetchUserDetails = async () => {
    try {
      const token = sessionStorage.getItem("userAccessToken");
      if (token) {
        const res = await userApi.get<{user: any}>('/profile', { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (res.data.user) {
          setUserDetails({
            name: res.data.user.name || '',
            email: res.data.user.email || '',
            age: res.data.user.age || '',
            phone: res.data.user.phone || ''
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch user details', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await mainAdminApi.get<{soundTherapies: SoundTherapySession[]}>('/view-sound-therapies');
      const sessionList = response.data.soundTherapies || [];
      setSessions(sessionList);
      // Don't auto-select - let user see list first
    } catch (err) {
      console.error("Failed to fetch session data:", err);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
    fetchSessions();
  }, []);

  const handleBookingSuccess = (bookingId: string) => {
    toast.success('Your sound therapy session has been booked!');
    setShowBookingModal(false);
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[#F8F7F4]">
        <div className="text-center">
          <div className="text-7xl mb-6">🧘‍♀️</div>
          <h2 className="text-4xl font-bold text-gray-800 mb-4">No Sessions Available</h2>
          <p className="text-gray-600 mb-6">Currently, there are no active sound therapy sessions.</p>
          <button
            onClick={() => navigate('/Sound-therapy')}
            className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition"
          >
            Back to Sound Therapy
          </button>
        </div>
      </div>
    );
  }

  // If no session is selected, show list of all sessions
  if (!selectedSession) {
    return (
      <div className="bg-[#F8F7F4] min-h-screen">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <button
              onClick={() => navigate('/Sound-therapy')}
              className="flex items-center text-teal-600 hover:text-teal-700 font-medium mb-4"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Sound Therapy
            </button>
            <h1 className="text-4xl font-bold text-gray-900">Available Sound Therapy Sessions</h1>
            <p className="text-gray-600 mt-2">{sessions.length} sessions available</p>
          </div>
        </div>

        {/* Sessions Grid */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sessions.map((session) => (
              <div
                key={session._id}
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-2xl transition-all transform hover:-translate-y-2"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-teal-400 to-teal-600 h-32 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="text-5xl mb-2">🎵</div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {session.therapyType === 'Group' ? 'Group Session' : 'Personal Session'}
                  </h3>
                  <p className="text-teal-600 font-semibold mb-4">
                    with {session.instructorName}
                  </p>

                  {/* Details */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Calendar className="h-4 w-4 text-teal-600" />
                      <span className="text-sm">
                        {new Date(session.date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Clock className="h-4 w-4 text-teal-600" />
                      <span className="text-sm">
                        {session.timing} ({session.day})
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Users className="h-4 w-4 text-teal-600" />
                      <span className="text-sm">
                        {session.therapyType}
                      </span>
                    </div>
                  </div>

                  {/* Price and Button */}
                  <div className="border-t pt-4 flex items-center justify-between">
                    <div className="text-2xl font-bold text-teal-600">
                      ₹{session.price}
                    </div>
                    <button
                      onClick={() => setSelectedSession(session)}
                      className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8F7F4]">
      {/* Header with Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => setSelectedSession(null)}
              className="flex items-center text-teal-600 hover:text-teal-700 font-medium"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Sessions
            </button>
            <span className="text-gray-400">•</span>
            <button
              onClick={() => navigate('/Sound-therapy')}
              className="flex items-center text-teal-600 hover:text-teal-700 font-medium"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Services
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Hero Image / Visual */}
            <div className="bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl h-96 mb-8 flex items-center justify-center overflow-hidden">
              <div className="text-center text-white">
                <div className="text-6xl mb-4">🎵</div>
                <h1 className="text-4xl font-bold">Sound Therapy Session</h1>
              </div>
            </div>

            {/* Session Details */}
            <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {selectedSession.therapyType === 'Group' ? 'Group Sound Therapy' : 'Personal Sound Therapy'}
              </h2>
              <p className="text-teal-600 font-semibold mb-4">with {selectedSession.instructorName}</p>
              
              {/* Quick Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-teal-600" />
                  <div>
                    <div className="text-sm text-gray-600">Date & Time</div>
                    <div className="font-semibold text-gray-900">
                      {new Date(selectedSession.date).toLocaleDateString()} at {selectedSession.timing}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-teal-600" />
                  <div>
                    <div className="text-sm text-gray-600">Price</div>
                    <div className="font-semibold text-gray-900">₹{selectedSession.price}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-teal-600" />
                  <div>
                    <div className="text-sm text-gray-600">Session Type</div>
                    <div className="font-semibold text-gray-900">{selectedSession.therapyType}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-teal-600" />
                  <div>
                    <div className="text-sm text-gray-600">Day</div>
                    <div className="font-semibold text-gray-900">{selectedSession.day}</div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">About This Session</h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Experience the transformative power of sound healing with {selectedSession.instructorName}. 
                  This immersive session uses authentic therapeutic instruments to create harmonic frequencies 
                  that resonate with your body's natural vibration. Promoting deep relaxation, stress relief, 
                  and energetic balance. Perfect for beginners and experienced practitioners alike.
                </p>
              </div>

              {/* Benefits */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Session Benefits</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    "Deep relaxation and stress relief",
                    "Energy balancing and alignment",
                    "Improved sleep quality",
                    "Mental clarity and focus",
                    "Emotional healing and release",
                    "Spiritual connection and awareness"
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                      <div className="h-2 w-2 bg-teal-600 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-700">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>


            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg sticky top-20">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold text-teal-600">₹{selectedSession.price}</span>
                  <span className="text-gray-600">/session</span>
                </div>
                <p className="text-gray-600 text-sm mb-2">
                  <strong>Instructor:</strong> {selectedSession.instructorName}
                </p>
                <p className="text-gray-600 text-sm mb-6">
                  <strong>Date:</strong> {new Date(selectedSession.date).toLocaleDateString()} at {selectedSession.timing}
                </p>
                
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="w-full bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 mb-3"
                >
                  Book Now
                </button>

                <div className="space-y-2 text-sm text-gray-600">
                  <p>✓ Instant confirmation</p>
                  <p>✓ Secure payment</p>
                  <p>✓ Expert practitioner</p>
                  <p>✓ Money-back guarantee</p>
                </div>
              </div>

              {/* Session Preview */}
              <div className="p-6">
                <h4 className="font-semibold text-gray-900 mb-3">What You'll Experience</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>🎵 Authentic therapeutic instruments</li>
                  <li>🧘 Guided meditation and breathing</li>
                  <li>✨ Energy alignment session</li>
                  <li>📿 Personalized sound healing</li>
                  <li>🌙 Relaxation journey included</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedSession && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <SoundTherapyBooking
              sessionData={{
                ...selectedSession,
                title: `Sound Therapy with ${selectedSession.instructorName}`,
                description: `${selectedSession.therapyType} Sound Therapy Session`,
                availableSlots: [selectedSession.timing]
              }}
              onClose={() => setShowBookingModal(false)}
              onBookingSuccess={handleBookingSuccess}
              sessionId={selectedSession._id}
              userDetails={userDetails}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SoundTherapyDetailsPage;
